import { useState, useEffect, useCallback, useRef } from "react";

import { InvoicePayment } from "../bitmaker/schemas/Billing/Payment";

import { useApiError } from "./useApiError";

import { BitmakerAPIClient } from "../services/BitmakerApp";

const normalizePayment = (payment: any): InvoicePayment => ({
  ...payment,
  amount: Number(payment.amount),
});

export const useInvoicePayments = (invoiceId: number) => {
  const [invoicePayments, setInvoicePayments] = useState<InvoicePayment[]>([]);
  const [loadingInvoicePayments, setLoadingInvoicePayments] = useState(false);
  const [fetchInvoicePaymentsError, setFetchInvoicePaymentsError] = useState<
    string | null
  >(null);

  const { handleAPIError } = useApiError();
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPayments = useCallback(() => {
    if (!invoiceId || !isMountedRef.current) return;

    setLoadingInvoicePayments(true);
    setFetchInvoicePaymentsError(null);

    BitmakerAPIClient.get(
      `/billing/invoices/${invoiceId}/payments`,
      {},
      (response) => {
        if (!isMountedRef.current) return;

        const { isError, message } = handleAPIError(response);

        if (isError) {
          console.error("Error fetching invoice payments:", message);
          setFetchInvoicePaymentsError(
            "Error fetching payments. Please try again.",
          );
          setLoadingInvoicePayments(false);
          return;
        }

        const data = Array.isArray(response.data) ? response.data : [];
        setInvoicePayments(data.map(normalizePayment));
        setLoadingInvoicePayments(false);
      },
    );
  }, [invoiceId, handleAPIError]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  return {
    invoicePayments,
    loadingInvoicePayments,
    fetchInvoicePaymentsError,
    refetchInvoicePayments: fetchPayments,
  };
};
